import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { NeoImageUploadComponent, NeoImageUploadInputs } from '@neogov/pattern-library/neo-image-upload';
import { AppConfigService } from '../../../core/config/app-config.service';
import { DialogProvider } from '@core';
import { GetAccessTokenUseCase } from '@auth';
import { AuthState } from '../../../auth/application/states/auth.state';
import { ProfileImageState } from '../../../core/providers/profile-image/profile-image.state';
import { AvatarComponent } from '../avatar';
import { ButtonComponent } from '../button';
import { SpinnerComponent } from '../spinner';

/**
 * Profile image uploader component.
 * Shows the current profile image in a circle with a "Change Photo" button.
 * On click, opens NeoImageUploadComponent in a NeoDialog modal (with crop & rotate support).
 * Reads the current image from the shared {@link ProfileImageState} so the navbar
 * avatar and this component stay in sync without either owning its own fetch.
 * Uses dedicated user profile image endpoints:
 * - POST /api/v1/users/me/profile-image (upload + update user record)
 * - GET /api/v1/users/me/profile-image (get image content, via ProfileImageState)
 */
@Component({
  selector: 'cm-profile-image-uploader',
  standalone: true,
  imports: [CommonModule, AvatarComponent, ButtonComponent, SpinnerComponent],
  templateUrl: './profile-image-uploader.component.html',
  styleUrl: './profile-image-uploader.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileImageUploaderComponent {
  private readonly _dialog = inject(DialogProvider);
  private readonly _configService = inject(AppConfigService);
  private readonly _getAccessTokenUseCase = inject(GetAccessTokenUseCase);
  private readonly _profileImageState = inject(ProfileImageState);
  private readonly _authState = inject(AuthState);

  /** Whether the user has a profile image (triggers load) */
  public readonly hasProfileImage = input<boolean>(false);

  /** Name of the user whose profile image is shown; used for the image alt text */
  public readonly userName = input<string>('');

  /** Descriptive alt text for the profile image */
  public readonly imageAlt = computed(() =>
    this.userName() ? `${this.userName()} profile photo` : 'Your profile photo',
  );

  /** Emitted when a new image is uploaded successfully */
  public readonly imageUploaded = output<string>();

  /** Blob URL of the current profile image (from the shared state). */
  public readonly imageUrl = this._profileImageState.imageUrl;
  /** True while the shared state is loading the image for the first time. */
  public readonly isLoadingImage = this._profileImageState.loading;

  constructor() {
    // Kick the shared state to fetch the image once the parent confirms the user
    // has one. The state keys the initial fetch on user.id — repeated calls with
    // the same id are a no-op; a user change (SPA login flip) forces a refetch.
    effect(() => {
      const userId = this._authState.user()?.id;
      if (this.hasProfileImage() && userId) {
        this._profileImageState.ensureLoaded(userId);
      }
    });
  }

  /**
   * Opens the NeoImageUploadComponent in a dialog modal.
   * Uploads directly to POST /api/v1/users/me/profile-image
   */
  public openUploadDialog(): void {
    const apiUrl = this._configService.versionedApiUrl;
    const token = this._getAccessTokenUseCase.execute()() || '';

    const headers: { name: string; value: string }[] = [
      { name: 'productCode', value: this._configService.productCode },
    ];
    if (token) {
      headers.push({ name: 'Authorization', value: `Bearer ${token}` });
    }

    const dialogData: Partial<NeoImageUploadInputs> = {
      uploadUrl: `${apiUrl}/users/me/profile-image`,
      autoUpload: true,
      maxFileSizeMB: 5,
      headers,
      autId: 'profile-image-upload',
      uploadAriaLabel: 'Upload profile image',
    };

    const dialogRef = this._dialog.open(NeoImageUploadComponent, {
      data: dialogData,
      width: '498px',
      panelClass: 'profile-image-upload-dialog',
    });

    const instance = dialogRef.componentInstance;

    if (!instance) {
      return;
    }

    instance.uploadedItem.subscribe((response: object) => {
      // NeoImageUploadComponent calls JSON.parse(response) internally before emitting, so
      // `response` here is already the parsed body (e.g. `{ documentId: '...' }`).
      const parsed = response as { documentId?: string };
      const documentId = parsed?.documentId ?? null;

      if (documentId) {
        this.imageUploaded.emit(documentId);
      }
      // Refresh the shared state — the navbar avatar and this component both
      // re-render off its signal, so the two views stay in sync automatically.
      this._profileImageState.refresh();
      dialogRef.close();
    });
  }
}
